import { useEffect, useRef, useState } from "react";
import type { NewsExplainerChart, NewsExplainerConfig } from "../scenario/types";
import { audio } from "../lib/audio";

/**
 * The newscast "data segment": after the breaking-news anchor, the camera pushes
 * in and the anchor (now a picture-in-picture portrait) walks through a few
 * full-screen data slides on the wider ransomware threat — the exploding global
 * cost, who gets hit, and how attacks almost always start with a person — each
 * with an animated chart and a spoken narration line. The learner advances slide
 * by slide, then continues to the quiz.
 *
 * Purely explanatory (no scoring). Narration uses the host TTS; if sound is off
 * it falls back to a timed read, and the Next/Continue buttons always let the
 * learner move at their own pace.
 */
export function NewsExplainer({
  config,
  onContinue,
  onSkip,
}: {
  config: NewsExplainerConfig;
  onContinue: () => void;
  onSkip?: () => void;
}) {
  const segments = config.segments ?? [];
  const [idx, setIdx] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [shown, setShown] = useState(false); // drives the chart grow-in
  const mounted = useRef(true);

  const seg = segments[idx];
  const last = idx >= segments.length - 1;

  useEffect(() => {
    mounted.current = true;
    // Warm upcoming slide narrations so Next doesn't stall on a cold TTS fetch.
    const voice = config.voiceId || "coral";
    const lang = config.language || "en-US";
    const instr =
      "Speak as a composed, authoritative television news anchor presenting a data segment. Clear, measured, serious but engaging.";
    for (const s of segments) {
      if (s?.narration) void audio.prefetchSpeech(s.narration, { voice, lang, instructions: instr });
    }
    return () => {
      mounted.current = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Narrate each slide as it appears; trigger the chart animation a tick later.
  useEffect(() => {
    if (!seg) return;
    setShown(false);
    const grow = window.setTimeout(() => mounted.current && setShown(true), 60);
    let alive = true;
    (async () => {
      setSpeaking(true);
      await audio.speak(seg.narration, {
        voice: config.voiceId || "coral",
        lang: config.language || "en-US",
        instructions:
          "Speak as a composed, authoritative television news anchor presenting a data segment. Clear, measured, serious but engaging.",
      });
      if (!audio.isEnabled()) {
        await new Promise<void>((r) =>
          window.setTimeout(r, Math.min(13000, 1600 + seg.narration.length * 34))
        );
      }
      if (alive && mounted.current) setSpeaking(false);
    })();
    return () => {
      alive = false;
      window.clearTimeout(grow);
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  if (!seg) return null;

  const next = () => {
    if (last) {
      audio.cancelSpeech();
      onContinue();
      return;
    }
    audio.cancelSpeech();
    setIdx((i) => Math.min(i + 1, segments.length - 1));
  };

  return (
    <div className="nx">
      <div className="nx-screen">
        {/* channel bug + LIVE */}
        <div className="nx-bug">{config.channel ?? "NBC24"}</div>
        <div className="nx-live">
          <span className="nx-live-dot" /> LIVE
        </div>

        {/* anchor picture-in-picture */}
        <div className="nx-pip">
          {config.posterUrl ? (
            <img src={config.posterUrl} alt="" className="nx-pip-img" />
          ) : (
            <div className="nx-pip-noface">{"\u{1F4FA}"}</div>
          )}
          <span className={`nx-pip-onair ${speaking ? "on" : ""}`}>
            <i /> <i /> <i />
          </span>
        </div>

        {/* the data slide */}
        <div className="nx-stage" key={idx}>
          {seg.kicker && <div className="nx-kicker">{seg.kicker}</div>}
          <h2 className="nx-title">{seg.title}</h2>
          <Chart chart={seg.chart} shown={shown} />
        </div>

        {/* lower third */}
        <div className="nx-lower">
          <div className="nx-lower-tag">{config.lowerThird ?? "RANSOMWARE EXPLAINED"}</div>
          <div className="nx-lower-src">
            {("source" in seg.chart && seg.chart.source) || config.anchorName || ""}
          </div>
        </div>
      </div>

      {/* controls */}
      <div className="nx-controls">
        <div className="nx-dots" aria-hidden>
          {segments.map((_, i) => (
            <span key={i} className={`nx-dot ${i === idx ? "on" : ""} ${i < idx ? "done" : ""}`} />
          ))}
        </div>
        {config.skipLabel && onSkip && (
          <button
            className="btn news-skip"
            onClick={() => {
              audio.cancelSpeech();
              onSkip();
            }}
          >
            {config.skipLabel}
          </button>
        )}
        <button className="btn nx-next" onClick={next}>
          {last ? config.continueLabel ?? "Continue" : "Next \u203A"}
        </button>
      </div>
    </div>
  );
}

function Chart({ chart, shown }: { chart: NewsExplainerChart; shown: boolean }) {
  if (chart.type === "stat") {
    return (
      <div className="nx-stat">
        <div className="nx-stat-big">{chart.big}</div>
        <div className="nx-stat-sub">{chart.sub}</div>
        {chart.bullets && chart.bullets.length > 0 && (
          <ul className="nx-stat-list">
            {chart.bullets.map((b, i) => (
              <li key={i} style={{ transitionDelay: `${i * 90}ms`, opacity: shown ? 1 : 0 }}>
                {b}
              </li>
            ))}
          </ul>
        )}
        {chart.caption && <div className="nx-cap">{chart.caption}</div>}
      </div>
    );
  }

  const max = Math.max(...chart.bars.map((b) => b.value)) || 1;

  if (chart.type === "hbars") {
    return (
      <div className="nx-hbars">
        {chart.bars.map((b, i) => (
          <div className="nx-hrow" key={i}>
            <span className="nx-hlabel">{b.label}</span>
            <span className="nx-htrack">
              <span
                className={`nx-hfill ${b.hot ? "hot" : ""}`}
                style={{ width: shown ? `${(b.value / max) * 100}%` : "0%", transitionDelay: `${i * 70}ms` }}
              />
            </span>
            <span className="nx-hval">{b.display}</span>
          </div>
        ))}
        {chart.caption && <div className="nx-cap">{chart.caption}</div>}
      </div>
    );
  }

  // vertical bars
  return (
    <div className="nx-bars-wrap">
      <div className="nx-bars">
        {chart.bars.map((b, i) => (
          <div className="nx-bcol" key={i}>
            <span className="nx-bval" style={{ opacity: shown ? 1 : 0, transitionDelay: `${i * 90 + 250}ms` }}>
              {b.display}
            </span>
            <span
              className={`nx-bbar ${b.hot ? "hot" : ""}`}
              style={{ height: shown ? `${Math.max(2, (b.value / max) * 100)}%` : "0%", transitionDelay: `${i * 90}ms` }}
            />
            <span className="nx-blabel">{b.label}</span>
          </div>
        ))}
      </div>
      {chart.caption && <div className="nx-cap">{chart.caption}</div>}
    </div>
  );
}
